import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Download, Share, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { OFundedIcon } from "@/components/BrandLogo";

/* Smart PWA install prompt.
 *
 * Strategy:
 *   • Chrome / Edge / Android — listen for `beforeinstallprompt`, stash
 *     the event, show our styled banner. Tap "Install" → call prompt().
 *   • iOS Safari — no programmatic install API. Detect Safari + iOS,
 *     show a custom tooltip with Share → Add to Home Screen instructions.
 *   • Hide entirely if the site is already running in standalone mode,
 *     on admin/auth routes, or if the user dismissed within the last
 *     14 days.
 *
 * Trigger fires when EITHER condition is met:
 *   • User has loaded their 2nd page in this session, OR
 *   • User has been engaged on the homepage for ≥20s.
 *
 * Stored state in localStorage keeps the UX gentle — no nagging. */

const DISMISS_KEY = "ofunded:install-dismissed-at";
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;   // 14 days
const PAGE_COUNT_KEY = "ofunded:page-count";
const PAGE_COUNT_THRESHOLD = 2;
const TIME_THRESHOLD_MS = 20_000;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/i.test(ua);
  const safari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  return ios && safari;
}

function recentlyDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return ts && Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function bumpPageCount() {
  try {
    const n = parseInt(localStorage.getItem(PAGE_COUNT_KEY) || "0", 10) + 1;
    localStorage.setItem(PAGE_COUNT_KEY, String(n));
    return n;
  } catch {
    return 1;
  }
}

export default function InstallPrompt() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);  // beforeinstallprompt event
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState("auto");  // "auto" | "ios"

  // 1. Increment page-count whenever the route changes.
  useEffect(() => {
    bumpPageCount();
  }, [pathname]);

  // 2. Capture beforeinstallprompt globally so we can re-use it later.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // 3. Auto-hide if the user installs via the address-bar UI.
  useEffect(() => {
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  // 4. Decide whether (and when) to show the banner.
  useEffect(() => {
    if (isStandalone()) return;                    // already installed
    if (pathname.startsWith("/admin") || pathname.startsWith("/auth")) return;
    if (recentlyDismissed()) return;

    const isIos = isIosSafari();
    // Chrome/Android needs `deferred`; iOS only needs detection.
    if (!deferred && !isIos) return;

    setVariant(isIos ? "ios" : "auto");

    const pageCount = parseInt(localStorage.getItem(PAGE_COUNT_KEY) || "0", 10);
    if (pageCount >= PAGE_COUNT_THRESHOLD) {
      setVisible(true);
      return;
    }
    const t = setTimeout(() => setVisible(true), TIME_THRESHOLD_MS);
    return () => clearTimeout(t);
  }, [deferred, pathname]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();   // user said no — respect that for 14 days
      }
    } catch {
      dismiss();
    } finally {
      setDeferred(null);
    }
  }, [deferred, dismiss]);

  if (!visible) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 28 }}
          className="fixed left-3 right-3 sm:left-auto sm:right-6 bottom-[88px] sm:bottom-6 z-[65] max-w-md sm:max-w-sm"
          data-testid="install-prompt"
          role="dialog"
          aria-label="Install OFunded app"
        >
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#070d10]/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] p-4 pr-3">
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top_left,rgba(170,222,235,0.12),transparent_60%)]" />

            <div className="relative flex items-start gap-3">
              <div className="shrink-0 w-11 h-11 rounded-xl bg-[#0d171c] border border-[#AADEEB]/25 grid place-items-center">
                <OFundedIcon className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">
                  {t("install.title", "Install OFunded")}
                </div>
                {variant === "ios" ? (
                  <p className="text-[12px] text-zinc-400 leading-snug mt-1">
                    {t("install.ios_part1", "Tap")} <Share size={12} className="inline -mt-0.5 mx-0.5 text-[#AADEEB]" /> {t("install.ios_part2", "in Safari, then")} <span className="text-zinc-200 font-medium">{t("install.ios_action", "Add to Home Screen")}</span> <Plus size={12} className="inline -mt-0.5 mx-0.5 text-[#AADEEB]" /> {t("install.ios_part3", "for one-tap access.")}
                  </p>
                ) : (
                  <p className="text-[12px] text-zinc-400 leading-snug mt-1">
                    {t("install.body", "Add the app to your home screen — one tap to challenges, payouts and your account.")}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={dismiss}
                aria-label={t("install.dismiss", "Dismiss install prompt")}
                data-testid="install-prompt-dismiss"
                className="shrink-0 p-1.5 -m-1 rounded-md text-zinc-500 hover:text-zinc-200 hover:bg-white/5 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {variant === "auto" && (
              <div className="relative mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={install}
                  data-testid="install-prompt-cta"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[#AADEEB] text-[#050b0e] text-sm font-medium hover:bg-[#c1e7f0] transition-colors"
                >
                  <Download size={14} />
                  {t("install.cta", "Install")}
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="px-3 py-2 rounded-full text-xs font-medium text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  {t("install.not_now", "Not now")}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
